import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
    Trash2, 
    Download,
    FileText,
    FileType,
    FileCode,
    File as FileIcon,
    Calendar,
    HardDrive,
    ShieldAlert
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { formatFileSize } from '../utils';
import type { FileDetails } from '@/api/files/files.types';
import { useAuth } from '@/context/AuthContext';
import { FadeId } from '@/components/ui/fade-id';

interface FileCardProps {
    file: FileDetails;
    onDelete: (id: string) => void;
    variants?: any;
}

interface FileTypeConfig {
    icon: typeof FileIcon;
    color: string;
    label: string;
}

const getFileTypeConfig = (mimeType: string): FileTypeConfig => {
    switch (mimeType) {
        case 'application/pdf':
            return {
                icon: FileType,
                color: 'text-red-500 dark:text-red-400',
                label: 'PDF'
            };
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return {
                icon: FileText,
                color: 'text-blue-500 dark:text-blue-400',
                label: 'DOC'
            };
        case 'text/markdown':
            return {
                icon: FileCode,
                color: 'text-purple-500 dark:text-purple-400',
                label: 'MD'
            };
        case 'text/plain':
            return {
                icon: FileText,
                color: 'text-gray-500 dark:text-gray-400',
                label: 'TXT'
            };
        default:
            return {
                icon: FileIcon,
                color: 'text-gray-500 dark:text-gray-400',
                label: 'FILE'
            };
    }
};

// StatButton Component - Same as other cards
const StatButton = ({ 
    icon: Icon, 
    value, 
    label, 
    tooltip,
    active = false
}: { 
    icon: any; 
    value: string | number; 
    label: string; 
    tooltip?: string;
    active?: boolean;
}) => {
    const content = (
        <div 
            className={`h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 hover:bg-muted/50 transition-colors cursor-default ${
                active ? 'bg-primary/5' : ''
            }`}
        >
            <div className="flex items-center gap-1.5 mb-0.5">
                <Icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-semibold truncate">{value}</span>
            </div>
            <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">{label}</span>
        </div>
    );

    if (tooltip) {
        return (
            <TooltipProvider delayDuration={200}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        {content}
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="text-xs">{tooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return content;
};

export const FileCard = ({ file, onDelete, variants }: FileCardProps) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const fileType = getFileTypeConfig(file.mimeType);
    const FileTypeIcon = fileType.icon;
    const { user } = useAuth();
    const canDeleteFiles = user?.permissions?.files === 'EDITOR';

    const handleDelete = async () => {
        if (!canDeleteFiles) {
            return;
        }
        try {
            setIsDeleting(true);
            await onDelete(file.id);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async () => {
        if (isDownloading) return;
        try {
            setIsDownloading(true);
            const response = await fetch(file.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.originalName || file.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            // console.error('Error downloading file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    // Format date
    const formattedDate = new Date(file.createdAt).toLocaleDateString();
    
    // Format size
    const sizeDisplay = formatFileSize(file.size);

    return (
        <motion.div variants={variants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, Date and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className={cn(
                                    "h-10 w-10 rounded-lg flex items-center justify-center",
                                    "bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm",
                                    fileType.color
                                )}>
                                    <FileTypeIcon className="h-5 w-5" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 
                                    className="text-base font-semibold tracking-tight truncate mb-1"
                                    title={file.name}
                                >
                                    {file.name}
                                </h3>
                                {/* Date */}
                                <div className="flex items-center gap-1.5 mt-1">
                                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">
                                        {formattedDate}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* File ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={file.id} 
                                label="File ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Single Row Grid - Type & Size */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-2 divide-x divide-border">
                            <StatButton
                                icon={FileText}
                                value={fileType.label}
                                label="Type"
                                tooltip={`File type: ${fileType.label}`}
                            />
                            
                            <StatButton
                                icon={HardDrive}
                                value={sizeDisplay}
                                label="Size"
                                tooltip={`File size: ${sizeDisplay}`}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex w-full rounded-lg border border-border overflow-hidden divide-x divide-border" onClick={(e) => e.stopPropagation()}>
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={isDownloading}
                                        className="flex-1 h-9 rounded-none hover:bg-muted gap-2"
                                    >
                                        {isDownloading ? (
                                            <Spinner className="h-4 w-4" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        <span className="text-xs font-medium">
                                            {isDownloading ? 'Downloading' : 'Download'}
                                        </span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">Download file</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleDelete}
                                        disabled={isDeleting || !canDeleteFiles}
                                        className="flex-1 h-9 rounded-none text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
                                    >
                                        {isDeleting ? (
                                            <Spinner className="h-4 w-4" />
                                        ) : (
                                            <Trash2 className="h-4 w-4" />
                                        )}
                                        <span className="text-xs font-medium">
                                            {isDeleting ? 'Deleting' : 'Delete'}
                                        </span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {canDeleteFiles ? (
                                        <p className="text-xs">Delete file</p>
                                    ) : (
                                        <div className="flex items-center gap-2 max-w-xs">
                                            <ShieldAlert className="w-12 h-12 text-amber-500" />
                                            <p>You need editor permissions to delete files. Contact your administrator for access.</p>
                                        </div>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
};
